/**
 * Dynamic-content tokens for inline `${…}` autocomplete.
 *
 * The node editor flattens the dynamic-content token set into autocomplete
 * suggestions once per panel and publishes them through this context, so every
 * `TokenizedInput` under the panel can offer those tokens inside the inline
 * `${…}` expression autocomplete — mirroring the token picker rail and the
 * field-fork popover. Publishing pre-flattened suggestions (rather than raw
 * sources) keeps the flatten cost at one-per-panel instead of one-per-field.
 * Otto expressions are JavaScript, so a token's insert text is its expression
 * body with the surrounding `${ }` stripped (the caret is already inside an
 * expression when the dropdown is open).
 */

import { createContext } from 'react';
import type { Token } from '@/types/valueSegment';
import type { TokenSource } from '@/types/tokenSource';
import type { AutocompleteSuggestion } from './ExpressionAutocomplete';

/** Stable empty default so consumers never see a fresh `[]` per render. */
const EMPTY_SUGGESTIONS: AutocompleteSuggestion[] = [];

/** Suggestions available to inline autocomplete; empty when unavailable. */
export const DynamicContentTokensContext =
  createContext<AutocompleteSuggestion[]>(EMPTY_SUGGESTIONS);

/**
 * Strip a leading `${` / `@{` and trailing `}` from a token expression. Only
 * strips when the value is a complete wrapper (both ends present and long
 * enough) so malformed values are returned untouched rather than truncated.
 */
function stripExpressionWrapper(value: string): string {
  const trimmed = value.trim();
  if (
    trimmed.length >= 3 &&
    (trimmed.startsWith('${') || trimmed.startsWith('@{')) &&
    trimmed.endsWith('}')
  ) {
    return trimmed.slice(2, -1);
  }
  return trimmed;
}

/** Cap the dropdown so a huge workflow can't produce an unbounded list. */
const MAX_TOKEN_SUGGESTIONS = 250;

/** Guard against pathologically deep / cyclic token trees. */
const MAX_TOKEN_DEPTH = 10;

/**
 * Flatten grouped token sources into autocomplete suggestions. Nested tokens
 * (object/array children) are included so deep paths are reachable; each
 * suggestion is grouped under its source name (Trigger, action name,
 * Parameters, Variables) so the dropdown mirrors the token picker sections.
 */
export function tokenSourcesToSuggestions(
  sources: TokenSource[] | undefined,
): AutocompleteSuggestion[] {
  if (!sources?.length) return [];

  const suggestions: AutocompleteSuggestion[] = [];
  const seen = new Set<string>();

  const visit = (tokens: Token[], category: string, depth: number): void => {
    if (depth > MAX_TOKEN_DEPTH) return;
    for (const token of tokens) {
      if (suggestions.length >= MAX_TOKEN_SUGGESTIONS) return;

      if (token.value) {
        const insertText = stripExpressionWrapper(token.value);
        const dedupeKey = `${category}\u0000${insertText}`;
        if (insertText && !seen.has(dedupeKey)) {
          seen.add(dedupeKey);
          suggestions.push({
            label: token.name || token.title || insertText,
            type: 'variable',
            detail: token.type ? `→ ${token.type}` : 'Dynamic content',
            ...(token.description ? { documentation: token.description } : {}),
            insertText,
            category,
            ...(token.type ? { returnType: token.type } : {}),
          });
        }
      }

      if (token.children?.length) {
        visit(token.children, category, depth + 1);
      }
    }
  };

  for (const source of sources) {
    visit(source.tokens, source.name, 0);
  }

  return suggestions;
}
